import React, { useState, useEffect } from "react";
import { firestore, storage } from "../../firebase-config";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSpinner, FaCheckCircle, FaTrash, FaTimesCircle, FaEdit, FaSave } from "react-icons/fa";
import "../../styles/administration.css";

const Administration = () => {
  const [members, setMembers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    if (uploadSuccess || uploadError) {
      const timer = setTimeout(() => {
        setUploadSuccess(false);
        setUploadError(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [uploadSuccess, uploadError]);

  const fetchMembers = async () => {
    try {
      const querySnapshot = await getDocs(collection(firestore, "administration"));
      let fetchedMembers = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by createdAt timestamp in ASCENDING order (oldest first)
      fetchedMembers.sort((a, b) => {
        const timeA = a.createdAt || 0;
        const timeB = b.createdAt || 0;
        return timeA - timeB; // Changed from timeB - timeA
      });

      setMembers(fetchedMembers);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Error fetching data.");
    }
  };

  const handleChange = (index, field, value) => {
    const updatedMembers = [...members];
    updatedMembers[index][field] = value;
    setMembers(updatedMembers);
  };

  const handleFileChange = (index, file) => {
    const updatedMembers = [...members];
    updatedMembers[index].image = file;
    updatedMembers[index].imagePreview = URL.createObjectURL(file);
    setMembers(updatedMembers);
  };

  const addNewMember = () => {
    setMembers([...members, { 
      name: "", 
      role: "", 
      qualification: "", 
      about: "", 
      image: null, 
      imageUrl: "",
      imagePreview: "" 
    }]);
  };

  const handleAddOrUpdate = async (index) => {
    const member = members[index];
    
    if (!member.name || !member.role) {
      toast.error("Name and Role are required!");
      return;
    }

    if (!member.image && !member.imageUrl) {
      toast.error("Please select an image!");
      return;
    }

    setUploading(true);
    setUploadSuccess(false);
    setUploadError(false);

    try {
      let imageUrl = member.imageUrl;

      // Upload new image if selected
      if (member.image) {
        const timestamp = Date.now();
        const storageRef = ref(storage, `administration/${member.name}_${timestamp}.jpg`);
        await uploadBytes(storageRef, member.image);
        imageUrl = await getDownloadURL(storageRef);

        // Delete old image if updating
        if (member.id && member.imageUrl) {
          try {
            const oldImageRef = ref(storage, member.imageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.log("Old image deletion failed or not found:", error);
          }
        }
      }

      const memberData = {
        name: member.name,
        role: member.role,
        qualification: member.qualification || "",
        about: member.about || "",
        imageUrl,
      };

      if (member.id) {
        // Update existing member (keep original createdAt)
        const docRef = doc(firestore, "administration", member.id);
        await updateDoc(docRef, memberData);
        toast.success("Member updated successfully!");
      } else {
        // Add new member with timestamp
        memberData.createdAt = Date.now();
        await addDoc(collection(firestore, "administration"), memberData);
        toast.success("Member added successfully!");
      }

      setUploadSuccess(true);
      setEditingIndex(null);
      await fetchMembers();
    } catch (error) {
      console.error("Operation failed:", error);
      setUploadError(true);
      toast.error("Operation failed: " + error.message);
    }
    setUploading(false);
  };

  const handleDelete = async (id, imageUrl, index) => {
    if (!window.confirm("Are you sure you want to delete this member?")) {
      return;
    }

    setUploading(true);
    try {
      // Delete from Firestore
      if (id) {
        await deleteDoc(doc(firestore, "administration", id));
      }

      // Delete from Storage
      if (imageUrl) {
        try {
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.log("Image deletion failed or not found:", error);
        }
      }

      toast.success("Deleted successfully");
      await fetchMembers();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Delete failed: " + error.message);
    }
    setUploading(false);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
  };

  const cancelEdit = (index) => {
    setEditingIndex(null);
    fetchMembers(); // Reset to original data
  };

  return (
    <div className="admin-panel">
      <h2>Admin Panel - Manage Members</h2>
      
      <button onClick={addNewMember} className="add-member-btn">
        + Add New Member
      </button>

      <div className="members-container">
        {members.map((member, index) => (
          <div key={index} className="member-box">
            <input
              type="text"
              placeholder="Name *"
              value={member.name}
              onChange={(e) => handleChange(index, "name", e.target.value)}
              disabled={editingIndex !== null && editingIndex !== index && member.id}
            />
            <input
              type="text"
              placeholder="Role *"
              value={member.role}
              onChange={(e) => handleChange(index, "role", e.target.value)}
              disabled={editingIndex !== null && editingIndex !== index && member.id}
            />
            <input
              type="text"
              placeholder="Qualification"
              value={member.qualification || ""}
              onChange={(e) => handleChange(index, "qualification", e.target.value)}
              disabled={editingIndex !== null && editingIndex !== index && member.id}
            />
            <textarea
              placeholder="About (Optional)"
              value={member.about || ""}
              onChange={(e) => handleChange(index, "about", e.target.value)}
              disabled={editingIndex !== null && editingIndex !== index && member.id}
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(index, e.target.files[0])}
              disabled={editingIndex !== null && editingIndex !== index && member.id}
            />
            
            {(member.imageUrl || member.imagePreview) && (
              <img 
                src={member.imagePreview || member.imageUrl} 
                alt="Preview" 
                className="preview-img" 
              />
            )}

            <div className="button-group">
              {member.id ? (
                <>
                  {editingIndex === index ? (
                    <>
                      <button 
                        className="save-btn" 
                        onClick={() => handleAddOrUpdate(index)}
                        disabled={uploading}
                      >
                        <FaSave /> Save
                      </button>
                      <button 
                        className="cancel-btn" 
                        onClick={() => cancelEdit(index)}
                        disabled={uploading}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        className="edit-btn" 
                        onClick={() => handleEdit(index)}
                        disabled={uploading || editingIndex !== null}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button 
                        className="delete-btn" 
                        onClick={() => handleDelete(member.id, member.imageUrl, index)}
                        disabled={uploading || editingIndex !== null}
                      >
                        <FaTrash /> Delete
                      </button>
                    </>
                  )}
                </>
              ) : (
                <button 
                  className="save-btn" 
                  onClick={() => handleAddOrUpdate(index)}
                  disabled={uploading}
                >
                  <FaSave /> Add Member
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Overlay UI */}
      {uploading && (
        <div className="overlay">
          <FaSpinner className="spin icon-large" />
        </div>
      )}

      {uploadSuccess && !uploading && (
        <div className="overlay success">
          <FaCheckCircle className="icon-large" />
        </div>
      )}

      {uploadError && !uploading && (
        <div className="overlay error">
          <FaTimesCircle className="icon-large" />
        </div>
      )}
    </div>
  );
};

export default Administration;